import { FormikValues } from 'formik'

import { momentDateHandler } from '../../date/moment-date-handler'
import { Tenant } from '../Tenants/models'

const { addMonths, isSameOrAfter, newDate } = momentDateHandler

type GlobalRole = 'admin' | 'bank' | 'business'
type TenantRole = 'admin' | 'finance' | 'viewer'

export interface User {
  id: number
  tenantId: number
  email: string
  firstName: string
  surname: string
  userStatus: string
  globalRole: GlobalRole
  tenantRole: TenantRole
  twoFactorEnabled: boolean
  lastLoginAt: string
  passwordExpiresAt: string
  userTenantStatus: string
  twoFactorAuthEnabled: boolean
}

export interface UserTenantTableItem {
  id?: number
  role?: string
  name?: string
  domain?: string
  temporaryId?: number
  status?: string
  isTrial?: boolean
  isDemo?: boolean
  nominateOnly?: boolean
}

export interface UserTableItem {
  id: number
  email: string
  name: string
  firstName: string
  surname: string
  tenants: UserTenantTableItem[]
  tenantNames?: string
  userStatus: string
  globalRole: string
  twoFactorEnabled: boolean
  lastLoginAt: string
  passwordExpiresAt: string
  loginStatus: string
  twoFactorAuthEnabled: boolean
  nominateOnly?: boolean
}

export interface UserFormValues extends FormikValues {
  id: number
  email: string
  firstName: string
  surname: string
  userStatus: string
  twoFactorAuthEnabled: boolean
}

export interface UserByTenant {
  tenantId: number
  role: string
  userId: number
  status?: string
}

export const userRoles = ['admin', 'finance', 'viewer']

export const userStatuses = ['enabled', 'disabled']

export const fromUserTenantTableToUserByTenant = ({
  userId,
  userTenants,
}: {
  userId: number
  userTenants: UserTenantTableItem[]
}): UserByTenant[] =>
  userTenants.map(({ id: tenantId, role }) => ({
    tenantId,
    role,
    userId,
  }))

export const loginStatuses = {
  neverLoggedIn: { color: 'lime', label: 'Never logged in', value: 'neverLoggedIn' },
  expired: { color: 'red', label: 'Expired (6mo)', value: 'expired' },
  aboutToExpire: { color: 'warning', label: 'About to expire (5mo)', value: 'aboutToExpire' },
  valid: { color: 'green', label: 'Valid', value: 'valid' },
}

const getLoginStatus = (lastLoginAt: string): string => {
  if (!lastLoginAt) {
    return loginStatuses.neverLoggedIn.value
  }

  const sixMonthsAgo = addMonths({ months: -6 })
  const fiveMonthsAgo = addMonths({ months: -5 })
  const lastLogin = newDate(new Date(lastLoginAt))

  if (isSameOrAfter({ firstDate: sixMonthsAgo, secondDate: lastLogin })) {
    return loginStatuses.expired.value
  }

  if (isSameOrAfter({ firstDate: fiveMonthsAgo, secondDate: lastLogin })) {
    return loginStatuses.aboutToExpire.value
  }

  return loginStatuses.valid.value
}

export const convertUsersToTableItems = ({ users, tenants }: { users: User[]; tenants: Tenant[] }): UserTableItem[] =>
  users
    .filter(({ globalRole }) => globalRole !== 'bank')
    .sort(byIdDesc)
    .reduce((userTableItems, currentUser) => {
      const user = userTableItems.find((user) => user.id === currentUser.id)
      const tenant = tenants.find(({ id }) => currentUser.tenantId === id)

      const tenantTableItem: UserTenantTableItem = {
        id: currentUser.tenantId,
        role: currentUser.tenantRole,
        name: tenant?.name,
        domain: tenant?.domain,
        isTrial: tenant?.isTrial,
        isDemo: tenant?.isDemo,
      }

      if (user) {
        user.tenants.push(tenantTableItem)

        return userTableItems
      }

      userTableItems.push(
        convertUserToTableItem({ user: currentUser, tenantTableItem: tenantTableItem.name && tenantTableItem }),
      )

      return userTableItems
    }, [])
    .map(addTenantNames)

const convertUserToTableItem = ({
  user,
  tenantTableItem,
}: {
  user: User
  tenantTableItem?: UserTenantTableItem
}): UserTableItem => ({
  ...user,
  name: `${user.firstName || ''} ${user.surname || ''}`,
  tenants: tenantTableItem ? [tenantTableItem] : [],
  loginStatus: getLoginStatus(user.lastLoginAt),
  globalRole: user.globalRole || undefined,
  twoFactorAuthEnabled: user.twoFactorAuthEnabled,
  nominateOnly: tenantTableItem?.nominateOnly,
})

const addTenantNames = (user: UserTableItem) => ({
  ...user,
  tenantNames: user.tenants.map((tenant) => tenant.domain).join(','),
})

const byIdDesc = (firstUser: User, secondUser: User) => (firstUser.id < secondUser.id ? 1 : -1)
